import React, { ReactNode, useEffect, useState } from 'react';
import {
  BrowserRouter as Router, Routes, Route, Navigate, useLocation,
} from 'react-router-dom';
import { Spinner } from './ui';
import { Shell, CommandPalette } from './layout';
import { ThemeProvider, useTheme } from './lib/ThemeContext';
import { PhrasesProvider, usePhrases } from './lib/PhrasesContext';
import { usePlainTextCopy } from './lib/usePlainTextCopy';
import { ToastProvider } from './lib/ToastContext';
import { useMarkdownSource } from './lib/useMarkdownSource';
import { LoginScreen } from './screens/LoginScreen';
import { OnboardingScreen } from './screens/OnboardingScreen';
import { HomeScreen } from './screens/HomeScreen';
import { PhraseDetail } from './screens/PhraseDetail';
import { FavoritesScreen } from './screens/FavoritesScreen';
import { FoldersScreen } from './screens/FoldersScreen';
import { SettingsScreen } from './screens/SettingsScreen';
import { GlossaryScreen } from './screens/GlossaryScreen';

function isAuthenticated(): boolean {
  try {
    return window.localStorage.getItem('isAuthenticated') === 'true';
  } catch (e) {
    return false;
  }
}

function RequireAuth({ children }: { children: ReactNode }) {
  const location = useLocation();
  // Carry the intended destination through the login round-trip so deep links
  // (/phrase/<id>, bookmarks, ⌘K-shared URLs) survive a cold session.
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function FullScreenLoader() {
  return (
    <div
      style={{
        height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-canvas)', color: 'var(--accent)',
      }}
    >
      <Spinner size={28} />
    </div>
  );
}

/** Inner app: requires phrases context; renders the app chrome + routes. */
function AppShell() {
  const { folders, favoriteIds } = usePhrases();
  const { accent } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // ⌘K / Ctrl-K toggles the global quick-open from anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Shell folders={folders} favoriteCount={favoriteIds.length} accent={accent}>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <Routes>
        <Route path="/" element={<Navigate to="/search" replace />} />
        <Route path="/search" element={<HomeScreen />} />
        <Route path="/phrase/:id" element={<PhraseDetail />} />
        <Route path="/favorites" element={<FavoritesScreen />} />
        <Route path="/folders" element={<FoldersScreen />} />
        <Route path="/folder/:parent" element={<HomeScreen />} />
        <Route path="/folder/:parent/:child" element={<HomeScreen />} />
        <Route path="/settings" element={<SettingsScreen />} />
        <Route path="/glossary" element={<GlossaryScreen />} />
        <Route path="*" element={<Navigate to="/search" replace />} />
      </Routes>
    </Shell>
  );
}

/** Loads the markdown library, then mounts the data + toast providers. */
function AuthedApp() {
  const { source, loading } = useMarkdownSource();
  if (loading) return <FullScreenLoader />;
  return (
    <PhrasesProvider source={source}>
      <ToastProvider>
        <AppShell />
      </ToastProvider>
    </PhrasesProvider>
  );
}

const App = () => {
  usePlainTextCopy(); // force all page copies to plain text (no styles/background)
  return (
  <ThemeProvider>
    <Router>
      <Routes>
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/onboarding" element={<Navigate to="/onboarding/1" replace />} />
        <Route path="/onboarding/:step" element={<OnboardingScreen />} />
        <Route
          path="/*"
          element={
            <RequireAuth>
              <AuthedApp />
            </RequireAuth>
          }
        />
      </Routes>
    </Router>
  </ThemeProvider>
  );
};

export default App;
